import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { Loader } from "@common/handleErrorAndLoading/Loader";
import { Modal } from "@common/Modal/Modal";
import { ICustomService } from "@common/SelectYourNumber/interface/ISubscription";
import { TITLE_INHERIT_STYLE } from "@constants";
import { useManageMobileServices } from "@features/ManageMobileServices/hook/useManageMobileServices";
import { Card, CardContent, CardHeader, Grid, Switch, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface IServiceState {
    isModalOpen: boolean;
    addonCode: string;
    status: boolean;
    setIsChecked: (checked: boolean) => void;
    toggleEvent: (code: string, isChecked: boolean, setCheckedState: (val: boolean) => void) => void;
}

interface IToggleParams {
    addonCode: string;
    isActive: boolean;
    setModalState: (value: IServiceState) => void;
}

interface IParams {
    selectedService: ICustomService;
}

const defaultModalState: IServiceState = {
    isModalOpen: false,
    addonCode: "",
    status: false,
    setIsChecked: () => {},
    toggleEvent: () => {},
};

const ServiceToggle = (props: IToggleParams) => {
    const { addonCode, isActive, setModalState } = props;
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const { postAddon, postAddonActionStatus } = useManageMobileServices();

    const handleToggleEvent = (code: string, isChecked: boolean, setCheckedState: (val: boolean) => void) => {
        postAddon(code, isChecked ? "ACTIVATION" : "DEACTIVATION", isChecked, setCheckedState);
    };

    const handleToggleChange = (status: boolean) => {
        setModalState({
            addonCode: addonCode,
            status: status,
            setIsChecked: setIsChecked,
            isModalOpen: true,
            toggleEvent: handleToggleEvent,
        });
    };

    useEffect(() => {
        setIsChecked(isActive);
    }, [isActive]);

    return postAddonActionStatus.isLoading ? (
        <Loader size={30} />
    ) : (
        <Switch
            checked={isChecked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleToggleChange(event.target.checked);
            }}
            id={addonCode}
        />
    );
};

const ManageServices = (props: IParams) => {
    const { selectedService } = props;
    const { getAddonsListState, addonsList, getAddonsList } = useManageMobileServices();
    const { t } = useTranslation("accountDetails");
    const [modalState, setModalState] = useState<IServiceState>(defaultModalState);

    useEffect(() => {
        getAddonsList();
    }, [selectedService]);

    const handleActionConfirmation = (addonCode: string, status: boolean, setIsChecked: (checked: boolean) => void) => {
        modalState.toggleEvent(addonCode, status, setIsChecked);
        setModalState(defaultModalState);
    };

    return (
        <Card elevation={2}>
            <CardHeader
                className="card-header card-header__right"
                title={t("manageServices")}
                titleTypographyProps={TITLE_INHERIT_STYLE}
            />
            <CardContent>
                <div className="card-header__circle" />
                <HandleErrorAndLoading
                    error={getAddonsListState.error}
                    isLoading={getAddonsListState.isLoading}
                    retryFetch={getAddonsList}
                >
                    <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                        {t("manageServicesInfo")}
                    </Typography>
                    {addonsList.map(({ name, active, addonCode }) => (
                        <Grid
                            item
                            container
                            key={addonCode}
                            xs={12}
                            direction="row"
                            justify="flex-start"
                            alignItems="center"
                            spacing={2}
                        >
                            <Grid item container direction={"column"} xs={5}>
                                <Typography align="right">{name}</Typography>
                                {addonCode === "ROC_aaS" && <Typography align="right">€0/{t("perMonth")}</Typography>}
                                {addonCode === "ANONYMOUS_CALL_REJECTION_aaS" && (
                                    <Typography align="right">€0.29/{t("perMonth")}</Typography>
                                )}
                            </Grid>
                            <Grid item>
                                <ServiceToggle addonCode={addonCode} isActive={active} setModalState={setModalState} />
                            </Grid>
                        </Grid>
                    ))}
                </HandleErrorAndLoading>
                <Modal
                    close={() =>
                        setModalState((prevState: IServiceState) => {
                            return { ...prevState, isModalOpen: false };
                        })
                    }
                    confirm={() =>
                        handleActionConfirmation(modalState.addonCode, modalState.status, modalState.setIsChecked)
                    }
                    content={t("toggleConfirmationMessage")}
                    displayCancel
                    isModalOpen={modalState.isModalOpen}
                    title={t("manageServices")}
                />
            </CardContent>
        </Card>
    );
};

export default ManageServices;
